import { observer } from 'mobx-react-lite';
import { ElementType, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { To, useNavigate, useParams } from 'react-router';
import { StepElement } from '../../../lib/components/compound/progress/stepper';
import { useStepper } from '../partials/actionStepperPartial';
import { BenefitConclusion } from './steps/conclusion';
import { BenefitProfile } from './steps/benefitProfile';
import { BenefitLogic } from './steps/benefitLogic';
import { useCreateCustomBenefit } from './hooks/useCreateCustomBenefit';
import { ConfirmationDialog } from '../../../lib/components';
import { enqueueSnackbar } from 'notistack';

export interface StepDef {
  content: FC;
  title?: string | null;
  sectionTitle?: string | null;
  stepperConfig?: Array<Omit<StepElement, 'icon' & { icon?: ElementType }>>;
  nextText?: string;
  hideNavigation?: boolean;
}

type CustomBenefitProcess = 'create_custom_benefit' | 'edit_custom_benefit';

export const CreateCustomBenefit = observer(() => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();

  const conclusion: StepDef = {
    content: BenefitConclusion,
    sectionTitle: null
  };

  const benefitProfile: StepDef = {
    content: BenefitProfile,
    sectionTitle: t('custom:describe_custom_benefit')
  };

  const benefitLogic: StepDef = {
    content: BenefitLogic,
    sectionTitle: t('custom:describe_custom_benefit_logic')
  };

  const createCustomBenefitStepper = [
    {
      title: t('custom:benefit_profile')
    },
    {
      title: t('custom:benefit_logic')
    }
  ];

  const step: Record<CustomBenefitProcess, Array<StepDef>> = {
    create_custom_benefit: [
      {
        ...benefitProfile,
        title: t('custom:create_custom_benefit'),
        stepperConfig: createCustomBenefitStepper
      },
      benefitLogic,
      conclusion
    ],
    edit_custom_benefit: [
      {
        ...benefitProfile,
        title: t('custom:edit_custom_benefit'),
        stepperConfig: createCustomBenefitStepper
      },
      benefitLogic,
      conclusion
    ]
  };

  const {
    setTitle,
    setSectionTitle,
    setStepperConfig,
    setBackToPath,
    setBackToTitle,
    setShowHelpButton,
    setCustomCancelHandler,
    setBackButtonConfig
  } = useStepper();

  const {
    newCustomBenefit,
    createCustomBenefit: create,
    patchCustomBenefit: patch
  } = useCreateCustomBenefit();

  const defaultStep = { ...step[params.type as CustomBenefitProcess][1], title: 'Custom Benefit' };
  const [currentStep, setCurrentStep] = useState<StepDef>(defaultStep);
  const [showCustomBenefitLeavingDialog, setShowCustomBenefitLeavingDialog] = useState(false);

  const saveAsDraft = async () => {
    if (!newCustomBenefit.name_de) {
      enqueueSnackbar({
        message: t('custom:error_missing_name'),
        variant: 'error'
      });
      return false;
    }

    try {
      if (newCustomBenefit.status === 'draft') {
        if (newCustomBenefit.editingBenefitId) {
          await patch(newCustomBenefit.editingBenefitId);
        }
      } else {
        newCustomBenefit.status = 'draft';
        await create();
      }

      enqueueSnackbar({
        message: t('custom:draft_submitted'),
        variant: 'info'
      });
      navigate('/benefits/custom');
      return true;
    } catch {
      enqueueSnackbar({
        message: t('custom:error_saving_draft'),
        variant: 'error'
      });
      return false;
    }
  };

  useEffect(() => {
    const handleCancel = (path: To) => {
      if (
        !currentStep.sectionTitle ||
        currentStep.sectionTitle === t('custom:submit_for_check') ||
        newCustomBenefit.changedFields.length === 0
      ) {
        newCustomBenefit.clear();
        navigate(path);
      } else {
        setShowCustomBenefitLeavingDialog(true);
      }
    };

    setCustomCancelHandler(() => handleCancel);

    return () => setCustomCancelHandler(null);
  }, [params.type, newCustomBenefit, navigate]);

  useEffect(() => {
    setStepperConfig(createCustomBenefitStepper);
    setBackToTitle(t('custom:back_to_overview'));
    setBackToPath('/benefits/custom');
    setBackButtonConfig({
      onClick: () => {
        newCustomBenefit.backButtonClicked = true;
      }
    });
  }, []);

  useEffect(() => {
    setShowHelpButton(
      [
        t('custom:describe_custom_benefit'),
        t('custom:assign_custom_benefit'),
        t('custom:describe_custom_benefit_logic')
      ].includes(currentStep.sectionTitle ?? '')
    );
  }, [currentStep]);

  useEffect(() => {
    if (params.type && !step[params.type as CustomBenefitProcess]) {
      navigate('/404');
    } else {
      const nextStep =
        params.type && params.step
          ? step[params.type as CustomBenefitProcess][+params.step - 1]
          : currentStep;
      setCurrentStep(nextStep);
      if (currentStep === defaultStep) newCustomBenefit.backButtonClicked = true;

      nextStep.title !== undefined && setTitle(nextStep.title ?? undefined);
      nextStep.sectionTitle !== undefined && setSectionTitle(nextStep.sectionTitle ?? undefined);
      nextStep.stepperConfig && setStepperConfig(nextStep.stepperConfig);
    }
  }, [params]);

  useEffect(() => {
    if (!newCustomBenefit.name_de) {
      if (params.type) {
        navigate(`/actions/custom-benefit/${params.type}/1`);
      } else {
        navigate('/benefits/custom');
      }
    }
  }, [navigate]);

  const Step = currentStep.content;

  return (
    <>
      <Step />
      <ConfirmationDialog
        open={showCustomBenefitLeavingDialog}
        title={t('custom:save_changes_as_draft')}
        contentText={t('custom:leave_without_saving_hint')}
        onConfirm={async () => {
          await saveAsDraft();
          setShowCustomBenefitLeavingDialog(false);
        }}
        onClose={() => {
          setShowCustomBenefitLeavingDialog(false);
        }}
        onCancel={() => {
          newCustomBenefit.clear();
          navigate('/benefits/custom');
        }}
        confirmButtonText={t('common:SAVE')}
        cancelButtonText={t('common:DISCARD')}
        variant={'question'}
      />
    </>
  );
});
